import { useEffect, useState } from 'react';
import { sellToExistingUser, sellUseToExistingUser } from '../../api/inventoryApi';
import { getQuantityInCanister } from '../../api/specimensApi';
import AccountSearch from '../../components/custom-input/AccountSearch';
import { FilterOption } from '../../components/custom-input/FilterDropdown';
import ValidatedInput, { ValidatedState } from '../../components/custom-input/ValidatedInput';
import Loader from '../../components/loader/Loader';
import SelectSpecimenFromCanister from '../../components/select-specimen-from-canister/SelectSpecimenFromCanister';
import SelectStorageItems from '../../components/select-storage-items/SelectStorageItems';
import SelectEmbryoStraws from '../../components/specimen-tables/SelectEmbryoStraws';
import MultiSelectDropdown from '../../components/custom-input/multiSelectDropdown';

import { BUTTON_CONSTANTS, INVENTORY_MANAGEMENT_CONSTANTS, LABEL_CONSTANTS, PAGE_HEADER_CONSTANTS } from '../../constants/common';
import { toastMessages } from '../../constants/errorMessages';
import { useAppSelector } from '../../redux/hooks';
import { showToast } from '../../services/toast.service';
import { StorageTankOptions } from '../../types/enums/storageTank.enum';
import { Validators } from '../../types/enums/validators.enum';
import { Account } from '../../types/interfaces/account.interfaces';
import { SpecimenInventory, EmbryoStraw } from '../../types/interfaces/specimen.interfaces';
import { SpecimenSelection } from '../../types/interfaces/inventoryTransaction.interfaces';
import { redirectToTopOfThePage } from '../../utils/commonUtils';

interface SelectedEmbryoStraw {
  embryoStrawId: number;
  embryoCountPerStraw?: number;
}

const InventoryManagementOnSiteSell = (): JSX.Element => {
  const user = useAppSelector(state => state.user);
  const initialStateSelect = { value: '', name: '' };
  const initialStateValidatedInput = { valid: false, value: '' };
  const [selectedSite, setSelectedSite] = useState(initialStateSelect);
  const [selectedTank, setSelectedTank] = useState(initialStateSelect);
  const [selectedCanister, setSelectedCanister] = useState(initialStateSelect);
  const [selectedOwner, setSelectedOwner] = useState<FilterOption<any>>(initialStateSelect);
  const [selectedSpecimen, setSelectedSpecimen] = useState<FilterOption<any>>(initialStateSelect);
  const [quantityInCanister, setQuantityInCanister] = useState<number>();
  const [quantityToSell, setQuantityToSell] = useState<ValidatedState>(initialStateValidatedInput);
  const [quantityToUse, setQuantityToUse] = useState<ValidatedState>(initialStateValidatedInput);
  const [notes, setNotes] = useState<string>('');
  const [buyerAccount, setBuyerAccount] = useState<Account>();
  const [usingNow, setUsingNow] = useState<boolean>();
  const [validate, setValidate] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedEmbryoStraws, setSelectedEmbryoStraws] = useState<SelectedEmbryoStraw[]>([]);
  const [specimenDetails, setSpecimenDetails] = useState<SpecimenInventory | null>(null);
  const [embryoStrawsToUse, setEmbryoStrawsToUse] = useState<FilterOption<number>[]>([]);
  const [totalEmbryoQuantity, setTotalEmbryoQuantity] = useState<number>(0);

  const clearForm = () => {
    setSelectedSite(initialStateSelect);
    setSelectedTank(initialStateSelect);
    setSelectedCanister(initialStateSelect);
    setSelectedOwner(initialStateSelect);
    setSelectedSpecimen(initialStateSelect);
    setQuantityInCanister(undefined);
    setQuantityToSell(initialStateValidatedInput);
    setQuantityToUse(initialStateValidatedInput);
    setNotes('');
    setBuyerAccount(undefined);
    setUsingNow(false);
    setValidate(false);
    setSelectedEmbryoStraws([]);
    setEmbryoStrawsToUse([]);
    setTotalEmbryoQuantity(0);
    setSpecimenDetails(null);
  };

  const isFemale = specimenDetails?.animal?.gender?.toLowerCase() === 'female';

  const handleSubmit = async (): Promise<void> => {
    setValidate(true);

    if (
      selectedSite.value &&
      selectedTank.value &&
      selectedCanister.value &&
      selectedOwner.value &&
      selectedSpecimen.value &&
      quantityToSell.valid &&
      buyerAccount?.accountId &&
      (!usingNow || (usingNow && quantityToUse.valid))
    ) {
      if (Number(selectedOwner.value) === buyerAccount.accountId) {
        showToast.error(toastMessages.SELLER_AND_BUYER_MUST_BE_DIFFERENT);
        return;
      }
      if (isFemale) {
        if (totalEmbryoQuantity !== +quantityToSell.value) {
          showToast.error(toastMessages.TOTAL_QUANTITY_MISMATCH_SELL);
          return;
        }
      }

      const sell = usingNow ? sellUseToExistingUser : sellToExistingUser;
      setIsLoading(true);

      try {
        const selectedEmbryoStrawIds = selectedEmbryoStraws.map(straw => straw.embryoStrawId);

        const totalSelectedQuantity = selectedEmbryoStraws.reduce(
          (sum, selectedStraw) => sum + (selectedStraw.embryoCountPerStraw || 0),
          0,
        );
        if (isFemale) {
          if (totalSelectedQuantity && totalSelectedQuantity !== +quantityToSell.value) {
            showToast.error(toastMessages.TOTAL_QUANTITY_MISMATCH_SELL);
            return;
          }
        }

        if (usingNow) {
          const embryoStrawsUsedFullData = specimenDetails?.embryoStraws?.filter(straw =>
            embryoStrawsToUse.some(sel => sel.value === straw.embryoStrawId),
          );

          const totalUsedQuantity =
            embryoStrawsUsedFullData?.reduce((sum, straw) => sum + (straw.embryoCountPerStraw || 0), 0) || 0;

          if (isFemale) {
            if (totalUsedQuantity !== +quantityToUse.value) {
              showToast.error(toastMessages.QUANTITY_USE_MISMATCH(quantityToUse.value, totalUsedQuantity));
              return;
            }

            const invalidStraws = embryoStrawsToUse.filter(e => !selectedEmbryoStrawIds.includes(e.value));

            if (invalidStraws.length > 0) {
              showToast.error(toastMessages.INVALID_STRAWS_USE);
              return;
            }
          }
        }

        const specimenSelections: SpecimenSelection[] = [
          {
            specimenId: specimenDetails?.specimenId || 0,
            selectedCanisterId: Number(selectedCanister.value),
            quantity: +quantityToSell.value,
            ...(usingNow && { quantityUsed: +quantityToUse.value }),
            ...(selectedEmbryoStrawIds.length > 0 ? { selectedEmbryoStrawIds } : {}),
            ...(embryoStrawsToUse.length > 0 ? { embryoStrawsToUse: embryoStrawsToUse.map(embryo => embryo.value) } : {}),
          },
        ];

        await sell({
          sellerAccountId: Number(selectedOwner.value),
          sellerUserId: user.userId,
          buyerAccountId: buyerAccount.accountId!,
          specimenSelections,
          notes,
        });

        showToast.success(toastMessages.SALE_HAS_BEEN_SUBMITTED);
        clearForm();
      } catch {
        showToast.error(toastMessages.FAILED_TO_SUBMIT_SALE);
      } finally {
        setIsLoading(false);
        redirectToTopOfThePage();
      }
    }
  };

  useEffect(() => {
    const fetchQuantity = async () => {
      try {
        const { data: specimenInventory } = await getQuantityInCanister(
          selectedCanister.value,
          selectedOwner.value,
          selectedSpecimen.value,
        );

        const specimenData = specimenInventory.find(s => s.specimenId === Number(selectedSpecimen.value));
        setSpecimenDetails(specimenData ?? null);

        const available = specimenData?.quantityAvailable ?? 0;
        const owned = specimenData?.quantityOwned ?? 0;
        setQuantityInCanister(Math.min(available, owned));
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        setQuantityInCanister(0);
      }
    };

    const shouldFetch = selectedCanister.value && selectedOwner.value && selectedSpecimen.value;
    shouldFetch ? fetchQuantity() : setQuantityInCanister(0);
  }, [selectedCanister, selectedSpecimen, selectedOwner]);

  const embryoStrawOptions: FilterOption<number>[] =
    specimenDetails?.embryoStraws
      ?.filter(straw => selectedEmbryoStraws.some(selected => selected.embryoStrawId === straw.embryoStrawId))
      .map((straw: EmbryoStraw) => ({
        name: straw.embryoStrawCode,
        value: straw.embryoStrawId,
      })) || [];

  useEffect(() => {
    if (totalEmbryoQuantity > 0) {
      setQuantityToSell({ valid: true, value: totalEmbryoQuantity.toString() });
    } else if (selectedEmbryoStraws.length === 0) {
      setQuantityToSell(initialStateValidatedInput);
    }

    if (usingNow && embryoStrawsToUse.length > 0) {
      const selectedStrawIds = selectedEmbryoStraws.map(s => s.embryoStrawId);
      const filteredEmbryoStrawsToUse = embryoStrawsToUse.filter(straw => selectedStrawIds.includes(straw.value));

      if (filteredEmbryoStrawsToUse.length !== embryoStrawsToUse.length) {
        setEmbryoStrawsToUse(filteredEmbryoStrawsToUse);
      }
    }
  }, [totalEmbryoQuantity, selectedEmbryoStraws, usingNow, embryoStrawsToUse]);

  useEffect(() => {
    if (embryoStrawsToUse.length > 0 && specimenDetails?.embryoStraws) {
      const embryoStrawsUsedFullData = specimenDetails.embryoStraws.filter(straw =>
        embryoStrawsToUse.some(sel => sel.value === straw.embryoStrawId),
      );

      const totalUsedQuantity = embryoStrawsUsedFullData.reduce((sum, straw) => sum + (straw.embryoCountPerStraw || 0), 0) || 0;

      setQuantityToUse({ valid: true, value: totalUsedQuantity.toString() });
    } else if (embryoStrawsToUse.length === 0 && usingNow) {
      setQuantityToUse(initialStateValidatedInput);
    }
  }, [embryoStrawsToUse, specimenDetails?.embryoStraws, usingNow]);

  return (
    <div className="inventory-management inventory-management-discard">
      {isLoading ? (
        <Loader loaderSize={'large'} pageLoader simple={true} />
      ) : (
        <div className="inventory-action card">
          <h1>{PAGE_HEADER_CONSTANTS.ON_SITE_SELL}</h1>
          <h2>{LABEL_CONSTANTS.SELL_INFO}</h2>
          <SelectStorageItems
            selectedSite={selectedSite}
            selectedTank={selectedTank}
            selectedCanister={selectedCanister}
            setSelectedSite={setSelectedSite}
            setSelectedTank={setSelectedTank}
            setSelectedCanister={setSelectedCanister}
            storageTankOptions={StorageTankOptions.Travel}
            activeLocationsOnly
          />
          <SelectSpecimenFromCanister
            canisterId={Number(selectedCanister.value)}
            setSelectedSpecimen={setSelectedSpecimen}
            selectedSpecimen={selectedSpecimen}
            selectedOwner={selectedOwner}
            setSelectedOwner={setSelectedOwner}
          />
          <div className="form-row">
            <label>{LABEL_CONSTANTS.QTY_IN_CANISTER}:</label>
            <div className="input-container">
              <label>{selectedOwner.value ? quantityInCanister : '-'}</label>
            </div>
          </div>

          {specimenDetails?.animal?.gender?.toLowerCase() === 'female' && specimenDetails?.embryoStraws && (
            <SelectEmbryoStraws
              embryoStraws={specimenDetails.embryoStraws}
              onChange={(selected: SelectedEmbryoStraw[], total: number) => {
                setSelectedEmbryoStraws(selected);
                setTotalEmbryoQuantity(total);
              }}
            />
          )}

          <div className="form-row">
            <ValidatedInput
              useId="quantity-to-sell"
              label="Quantity To Sell:"
              type="number"
              min={1}
              max={quantityInCanister}
              setValidatedState={setQuantityToSell}
              validators={[Validators.REQUIRED]}
              validate={validate}
              disabled={!quantityInCanister}
              validatedStateForAutoFill={quantityToSell}
            />
          </div>

          <div className="form-row expanded-text-area">
            <label htmlFor="inventory-notes-textarea">{LABEL_CONSTANTS.NOTES}:</label>
            <textarea value={notes} placeholder="Notes" id="inventory-notes-textarea" onChange={e => setNotes(e.target.value)} />
          </div>
          <br />
          <h2>{LABEL_CONSTANTS.BUYER_INFO}</h2>
          <div className="form-row">
            <label>{LABEL_CONSTANTS.BUYER_ACCOUNT}</label>
            <div className="input-container">
              <AccountSearch showIcon onChange={(account: Account | undefined) => setBuyerAccount(account)} />
            </div>
          </div>
          <div className="form-row">
            <label>{INVENTORY_MANAGEMENT_CONSTANTS.ON_SITE_SELL_QUESTION}</label>
            <div className="input-container">
              <div className="radio-row">
                <input type="radio" id="use-now-true" name="use-now" onChange={() => setUsingNow(true)} />
                <label htmlFor="use-now-true">{LABEL_CONSTANTS.YES}</label>
                <input type="radio" id="use-now-false" name="use-now" defaultChecked={true} onChange={() => setUsingNow(false)} />
                <label htmlFor="use-now-false">{LABEL_CONSTANTS.NO}</label>
              </div>
            </div>
          </div>

          {usingNow && (
            <>
              <h2>{LABEL_CONSTANTS.USE_INFO}</h2>

              {selectedEmbryoStraws?.length ? (
                <div className="form-row">
                  <label>{LABEL_CONSTANTS.EMBRYO_STRAWS_TO_USE}:</label>
                  <div className="input-container">
                    <MultiSelectDropdown
                      options={embryoStrawOptions}
                      value={embryoStrawsToUse}
                      onChange={setEmbryoStrawsToUse}
                      placeholder="Select one or more straws"
                      validate={validate}
                    />
                  </div>
                </div>
              ) : null}

              {embryoStrawsToUse.length > 0 && (
                <div className="form-row">
                  <div className="select-storage-items">
                    <div className="straw-codes-summary">
                      <label>{LABEL_CONSTANTS.SELECTED_STRAW_LOTS}: </label>
                      <div className="straw-capsule-container">
                        {embryoStrawsToUse.map(straw => (
                          <span key={straw.value} className="straw-chip">
                            {straw.name}
                            <button
                              className="remove-chip"
                              onClick={() => {
                                const updatedStraws = embryoStrawsToUse.filter(s => s.value !== straw.value);
                                setEmbryoStrawsToUse(updatedStraws);
                              }}
                              type="button">
                              ✕
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="form-row">
                <ValidatedInput
                  useId="quantity-to-use"
                  label="Quantity To Use:"
                  type="number"
                  min={0}
                  max={+quantityToSell.value}
                  setValidatedState={setQuantityToUse}
                  validate={validate}
                  validators={[Validators.REQUIRED]}
                  validatedStateForAutoFill={quantityToUse}
                />
              </div>
            </>
          )}

          <div className="flex-right">
            <button
              className="button small green"
              onClick={handleSubmit}
              disabled={
                !(
                  selectedSite.value.length > 0 &&
                  selectedTank.value.length > 0 &&
                  selectedCanister.value.length > 0 &&
                  selectedOwner.value &&
                  selectedSpecimen.value &&
                  quantityToSell.valid &&
                  buyerAccount?.name &&
                  (!usingNow || (usingNow && quantityToUse.value))
                )
              }>
              {BUTTON_CONSTANTS.SUBMIT}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryManagementOnSiteSell;
